import { supabase } from '@/integrations/supabase/client';

/**
 * Generate a signed URL for a photo in storage
 * @param storagePath - The path in storage (e.g., "orgId/photoId.jpg")
 * @param expiresIn - Expiration time in seconds (default: 1 hour)
 * @returns Signed URL or null if error
 */
export async function generateSignedUrl(
  storagePath: string,
  expiresIn: number = 3600
): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from('photos')
      .createSignedUrl(storagePath, expiresIn);

    if (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Exception generating signed URL:', error);
    return null;
  }
}

/**
 * Generate signed URLs for multiple photos
 * @param storagePaths - Array of storage paths
 * @param expiresIn - Expiration time in seconds (default: 1 hour)
 * @returns Array of signed URLs (null for errors)
 */
export async function generateSignedUrls(
  storagePaths: string[],
  expiresIn: number = 3600
): Promise<(string | null)[]> {
  try {
    const { data, error } = await supabase.storage
      .from('photos')
      .createSignedUrls(storagePaths, expiresIn);

    if (error) {
      console.error('Error generating signed URLs:', error);
      return storagePaths.map(() => null);
    }

    return data.map(item => item.signedUrl);
  } catch (error) {
    console.error('Exception generating signed URLs:', error);
    return storagePaths.map(() => null);
  }
}

/**
 * Extract storage path from a public or signed URL
 * @param url - The URL to extract the path from
 * @returns Storage path (e.g., "orgId/photoId.jpg")
 */
export function extractStoragePath(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const photosIndex = pathParts.indexOf('photos');
    
    if (photosIndex === -1 || photosIndex >= pathParts.length - 1) {
      return null;
    }

    // Return everything after 'photos/'
    return pathParts.slice(photosIndex + 1).join('/');
  } catch (error) {
    console.error('Error extracting storage path:', error);
    return null;
  }
}

/**
 * Add or refresh signed URLs for an array of photos
 * Handles both old public URLs and expired signed URLs
 * @param photos - Array of photo objects with url property
 * @param expiresIn - Expiration time in seconds (default: 1 hour)
 * @returns Array of photos with refreshed signed URLs
 */
export async function addSignedUrlsToPhotos<T extends { url: string; id: string }>(
  photos: T[],
  expiresIn: number = 3600
): Promise<T[]> {
  if (photos.length === 0) {
    return photos;
  }

  try {
    // Extract storage paths from existing URLs
    const storagePaths = photos.map(photo => extractStoragePath(photo.url)).filter(Boolean) as string[];
    
    if (storagePaths.length === 0) {
      console.warn('No valid storage paths found in photos');
      return photos;
    }

    // Generate signed URLs in batch
    const signedUrls = await generateSignedUrls(storagePaths, expiresIn);

    // Map signed URLs back to photos
    return photos.map((photo, index) => {
      const signedUrl = signedUrls[index];
      if (signedUrl) {
        return { ...photo, url: signedUrl };
      }
      return photo; // Keep original URL if signing failed
    });
  } catch (error) {
    console.error('Error adding signed URLs to photos:', error);
    return photos; // Return original photos if process fails
  }
}
